import React, {useState, useRef, useContext} from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  FormControl,
  FormGroup,
  FormHelperText,
  Input,
  InputLabel,
  IconButton,
  Typography,
} from '@material-ui/core';
import {Close as CloseIcon} from '@material-ui/icons';
import {formShape} from 'rc-form';
import {isEmpty} from 'lodash/lang';
import ImageWrapper from '../ImageWrapper';
import {buildMediaFileUrl} from 'utils/common';
import {I18nContext} from 'contexts/i18n';

const useStyles = makeStyles(({spacing, palette}) => ({
  input: {
    display: 'none',
  },
  imageWrapper: {
    position: 'relative',
    marginTop: 20,
    cursor: 'pointer',
  },
  deleteButton: {
    position: 'absolute',
    top: spacing(1),
    right: spacing(1),
    backgroundColor: palette.primary.light,
    '&:hover, &:focus': {
      backgroundColor: palette.primary.light,
    },
  },
  placeholder: {
    backgroundColor: palette.grey[200],
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 320,
  },
  placeholderText: {
    fontSize: 24,
    fontWeight: 100,
  },
}));

const ImageField = (props) => {
  const {i18nTranslate} = useContext(I18nContext);
  const classes = useStyles();
  const {
    name,
    label,
    rules,
    initialValue,
    helpText,
    width,
    height,
    form: {getFieldProps, getFieldError},
  } = props;
  let errors = getFieldError(name);
  let hasError = !isEmpty(errors);
  let error;
  if (hasError) {
    error = errors[0];
  }

  const inputFieldProps = getFieldProps(name, {
    validate: [
      {trigger: 'onBlur', rules},
      {trigger: 'onChange', rules},
    ],
    initialValue,
  });

  const [imageUrl, setImageUrl] = useState(initialValue);
  const fileInputRef = useRef();

  const _onFileInputClick = () => {
    fileInputRef.current.click();
  };

  const _deleteImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    inputFieldProps.onChange(null);
    setImageUrl(null);
  };

  const _onFileInputChange = ({target: {files}}) => {
    if (isEmpty(files)) {
      _deleteImage();
      return;
    }
    inputFieldProps.onChange(files[0]);
    const reader = new FileReader();
    reader.onload = ({target: {result}}) => {
      setImageUrl(result);
    };
    reader.readAsDataURL(files[0]);
  };

  return (
    <FormGroup className="mui-form-group">
      <FormControl error={hasError}>
        <InputLabel htmlFor={name} shrink>
          {label}
        </InputLabel>
        <Input
          id={name}
          name={name}
          className={classes.input}
          type="file"
          inputRef={fileInputRef}
          {...inputFieldProps}
          value=""
          onChange={_onFileInputChange}
          inputProps={{
            accept: 'image/*',
          }}
        />
        <ImageWrapper width={width} height={height} onClick={_onFileInputClick} className={classes.imageWrapper}>
          {!isEmpty(imageUrl) && (
            <IconButton
              variant="contained"
              color="primary"
              size="small"
              onClick={_deleteImage}
              className={classes.deleteButton}
            >
              <CloseIcon />
            </IconButton>
          )}
          {imageUrl ? (
            <img src={imageUrl.startsWith('data:image') ? imageUrl : buildMediaFileUrl({url: imageUrl})} />
          ) : (
            <div className={classes.placeholder}>
              <Typography className={classes.placeholderText}>{i18nTranslate`Click to select an image`}</Typography>
            </div>
          )}
        </ImageWrapper>
        {helpText && <FormHelperText error={false}>{helpText}</FormHelperText>}
        <FormHelperText error={hasError}>{error}</FormHelperText>
      </FormControl>
    </FormGroup>
  );
};

ImageField.propTypes = {
  form: formShape.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  rules: PropTypes.array.isRequired,
  initialValue: PropTypes.string,
  helpText: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

ImageField.defaultProps = {
  rules: [],
  initialValue: '',
  helpText: '',
  width: '100%',
  height: '100%',
};

export default ImageField;
